import { phpAuthUser } from "../../core/php-data-access";
import { searchSuggestApi } from "./search-suggest-api";
import { Patterns } from "../../constants/patterns";
import { SearchSuggestHandler, type SearchSuggestOptions } from "./search-suggest-handler";
import { CommonUtils } from "../../util/common-utils";
import { searchSuggestForm } from "./search-suggest-form";
import { SearchSuggestListener } from "./search-suggest-listener";

type SuggestSelectDetail = {
    type?: string;
    outputValue?: string;
    text?: string;
};

export class SearchSuggestSetup {
    public api = searchSuggestApi;

    public eventHandler = new SearchSuggestHandler(this.api);

    public init(rootEl?: Document | HTMLElement): void {
        const root = rootEl ?? document;
        this.initProductSearch(root);
        this.initBoardSearch(root);
        this.initBrandListSearch(root);
        this.initProductFilterKeywordSearch(root);
    }

    public initProductSearch(rootEl?: Document | HTMLElement): void {
        const root = rootEl ?? document;
        const inputs = searchSuggestForm.getProductSearchInputs(root);

        inputs.forEach((productSearchInput) => {
            // No sectionId restriction — post_title suggestions (beauty_news
            // articles) have metadata.section_id "general" (mapBoardIdToSectionId
            // has no entry for beauty_news), not "products", so scoping this to
            // "products" would silently exclude every article suggestion here.
            this.eventHandler.initialize(productSearchInput, {
                limit: 10,
                minChars: 2,
                autoSubmit: false,
            });

            if (productSearchInput.dataset.searchSuggestSelectBound === "1") return;
            productSearchInput.dataset.searchSuggestSelectBound = "1";

            SearchSuggestListener.bindSuggestSelect(productSearchInput, async (event) => {
                const detail = event instanceof CustomEvent ? (event.detail as SuggestSelectDetail | null) : null;
                const type = typeof detail?.type === "string" ? detail.type : "";
                const outputValue = typeof detail?.outputValue === "string" ? detail.outputValue : "";
                const text = typeof detail?.text === "string" ? detail.text : "";

                const userId = Number(phpAuthUser().user_id || 0);

                try {
                    await this.api.saveSearchHistory(userId, outputValue || text, type, "products");
                } catch (error) {
                    console.error("Failed to save search history:", error);
                }

                if (type === "product" && outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/products/${outputValue}`);
                } else if (type === "brand" && outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/brands/${outputValue}`);
                } else if (type === "category" && outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/products/category/${outputValue}`);
                } else if (type === "user" && outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/users/${outputValue}`);
                } else {
                    const form = searchSuggestForm.getClosestForm(productSearchInput);
                    if (form) {
                        const searchInput = searchSuggestForm.getNamedSearchInput(form);
                        if (searchInput instanceof HTMLInputElement) {
                            searchInput.value = text;
                        }
                        form.dispatchEvent(new Event("submit"));
                    }
                }
            });
        });
    }

    public initBoardSearch(rootEl?: Document | HTMLElement): void {
        const root = rootEl ?? document;
        const inputs = searchSuggestForm.getBoardSearchInputs(root);

        inputs.forEach((boardSearchInput) => {
            const boardId = this.getBoardIdFromPage(root);

            // boardId (exact match on metadata.board_id), not sectionId —
            // mapBoardIdToSectionId() buckets most non-tech boards (beauty_news,
            // editorial_reviews, ...) into the same "general" section, so a
            // sectionId-only scope would leak matches from the wrong board.
            this.eventHandler.initialize(boardSearchInput, {
                searchType: "post_title",
                boardId,
                limit: 10,
                minChars: 2,
                autoSubmit: false,
            });

            if (boardSearchInput.dataset.searchSuggestSelectBound === "1") return;
            boardSearchInput.dataset.searchSuggestSelectBound = "1";

            SearchSuggestListener.bindSuggestSelect(boardSearchInput, (event) => {
                const detail = event instanceof CustomEvent ? (event.detail as SuggestSelectDetail | null) : null;
                const text = typeof detail?.text === "string" ? detail.text : "";

                if (boardSearchInput instanceof HTMLInputElement) {
                    boardSearchInput.value = text;
                }
                const form = searchSuggestForm.getClosestForm(boardSearchInput);
                if (form) {
                    form.dispatchEvent(new Event("submit"));
                }
            });
        });
    }

    /**
     * The /brands keyword box (filter-sidebar.blade.php's
     * data-role="brands-sidebar-keyword") has no live suggestions today —
     * only the navbar's product search does. Restrict to searchType: "brand"
     * so results here are only brands, then jump straight to the selected
     * brand's page (mirrors the navbar's own brand-suggestion branch below).
     * This input has no wrapping <form> (Enter is handled by
     * brands-sidebar-setup.ts's own keydown listener, independently of this
     * one), so unlike initProductSearch/initBoardSearch there is no
     * form-submit fallback to reuse for a non-navigable selection.
     */
    public initBrandListSearch(rootEl?: Document | HTMLElement): void {
        const root = rootEl ?? document;
        const inputs = searchSuggestForm.getBrandListSearchInputs(root);

        inputs.forEach((brandSearchInput) => {
            this.eventHandler.initialize(brandSearchInput, {
                searchType: "brand",
                limit: 10,
                minChars: 2,
                autoSubmit: false,
            });

            if (brandSearchInput.dataset.searchSuggestSelectBound === "1") return;
            brandSearchInput.dataset.searchSuggestSelectBound = "1";

            SearchSuggestListener.bindSuggestSelect(brandSearchInput, (event) => {
                const detail = event instanceof CustomEvent ? (event.detail as SuggestSelectDetail | null) : null;
                const outputValue = typeof detail?.outputValue === "string" ? detail.outputValue : "";

                if (outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/brands/${outputValue}`);
                }
            });
        });
    }

    /**
     * The /products list, /brands/{id} detail page, AND /posts/{board_id}
     * board listing (base/posts/index.blade.php — its own, separate
     * board-search-section markup is commented out, this is the only keyword
     * search actually wired up there) all share the same filter-sidebar
     * keyword box (products/filter-sidebar.blade.php's
     * data-role="filter-sidebar-keyword-search").
     *
     * On /products and /brands/{id}, deliberately NOT restricted to
     * searchType: "product" or sectionId: "products" — brand/category/user
     * suggestions all have metadata.section_id "products", but post_title has
     * section_id "general" (mapBoardIdToSectionId has no per-board entries),
     * so a "products" sectionId restriction there would silently exclude
     * every article suggestion (see ISS-04-103). Matches the navbar's own
     * product-search box, which is unrestricted too.
     *
     * On /posts/{board_id} specifically, this is scoped down to
     * searchType: "post_title" (brand/product suggestions don't belong on an
     * article listing) AND boardId scoped to that exact board's
     * metadata.board_id — otherwise e.g. /posts/editorial_reviews would
     * suggest beauty_news articles, which is simply wrong there
     * (initBoardSearch()'s own boardId scoping never applied here since
     * board-search-section is commented out and this function handles the
     * input that's actually rendered instead).
     *
     * Selecting a product jumps to its page; selecting a brand jumps to the
     * brand's page (same as the navbar's brand-suggestion branch). If a
     * selection somehow has neither (always true on /posts/{board_id}, since
     * searchType there only ever yields post_title results), fall back to a
     * synthetic Enter keydown so the existing FilterSidebarListener
     * keyword-search handler (products/filter/filter-sidebar-listener.ts)
     * runs its normal text-search navigation instead of silently doing
     * nothing.
     */
    public initProductFilterKeywordSearch(rootEl?: Document | HTMLElement): void {
        const root = rootEl ?? document;
        const inputs = searchSuggestForm.getProductFilterKeywordInputs(root);
        const postsPathMatch = window.location.pathname.match(Patterns.PATHNAME_POSTS_ID);
        const currentBoardId = postsPathMatch ? postsPathMatch[1] : undefined;
        const isProductsListing = window.location.pathname.replace(/\/$/, "") === "/products";

        inputs.forEach((filterKeywordInput) => {
            this.eventHandler.initialize(filterKeywordInput, {
                // On /posts/{board_id} specifically, scope to articles only —
                // brand/product suggestions don't belong on an article listing
                // page. On /products specifically, the reverse: articles don't
                // belong in a product-shopping search (explicit request).
                // /brands/{id} stays unrestricted (includes articles).
                searchType: currentBoardId ? "post_title" : undefined,
                excludeTypes: isProductsListing ? ["post_title"] : undefined,
                boardId: currentBoardId,
                limit: 10,
                minChars: 2,
                autoSubmit: false,
            });

            if (filterKeywordInput.dataset.searchSuggestSelectBound === "1") return;
            filterKeywordInput.dataset.searchSuggestSelectBound = "1";

            SearchSuggestListener.bindSuggestSelect(filterKeywordInput, (event) => {
                const detail = event instanceof CustomEvent ? (event.detail as SuggestSelectDetail | null) : null;
                const type = typeof detail?.type === "string" ? detail.type : "";
                const outputValue = typeof detail?.outputValue === "string" ? detail.outputValue : "";

                if (type === "product" && outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/products/${outputValue}`);
                    return;
                }
                if (type === "brand" && outputValue) {
                    window.location.href = CommonUtils.resolveUrl(`/brands/${outputValue}`);
                    return;
                }

                filterKeywordInput.dispatchEvent(
                    new KeyboardEvent("keydown", { key: "Enter", bubbles: true, cancelable: true }),
                );
            });
        });
    }

    public getBoardIdFromPage(rootEl?: Document | HTMLElement): string {
        const root = rootEl ?? document;
        const pathMatch = window.location.pathname.match(Patterns.PATHNAME_POSTS_ID);
        if (pathMatch) {
            return pathMatch[1];
        }

        const form = searchSuggestForm.getBoardSearchForm(root) || searchSuggestForm.getBoardSearchForm(document);
        if (form instanceof HTMLElement && form.dataset.boardId) {
            return form.dataset.boardId;
        }

        return "general";
    }

    public mapBoardIdToSectionId(boardId: string): string {
        const mapping: Record<string, string> = {
            tech: "tech",
            programming: "programming",
            frontend: "frontend",
            devops: "devops",
            review: "products",
            general: "general",
        };

        return mapping[boardId] || "general";
    }

    public initCustom(inputElement: HTMLElement | string, options: SearchSuggestOptions = {}): void {
        const input =
            typeof inputElement === "string" ? searchSuggestForm.getElementBySelector(inputElement) : inputElement;

        if (input instanceof HTMLElement) {
            this.eventHandler.initialize(input, options);
        }
    }

    public static init(rootEl: Document | HTMLElement = document): void {
        const manager = new SearchSuggestSetup();
        manager.init(rootEl);
    }
}
